import {Typography} from '@material-ui/core';
import {Controller, ControllerRenderProps, FieldPath, useFormContext} from 'react-hook-form';
import {
    CurrencyFormatCustom,
    PercentageFormatCustom,
    PercentageFormatPreciseCustom,
    SwitchCustom,
    TextBox,
} from '../../../../Core/UI';
import {Parameter} from '../../../../Core/mobx/ParameterStore/ParameterStore';
import {SelectInputElement} from '../../../PreQualification/Components/SelectInputElement';
import {FormType} from '../../../PreQualification/types';
import {ProsperFormValues} from '../lib/validation';

export enum FormFieldType {
    NUMBER = 'number',
    CURRENCY = 'currency',
    PERCENTAGE = 'percentage',
    TEXT = 'text',
    TEXTAREA = 'textarea',
    SWITCH = 'switch',
    CHECKBOX = 'checkbox',
    DATE_PICKER = 'datePicker',
    SELECT = 'select',
    CUSTOM = 'custom',
}

type FormFieldProps<T extends FieldPath<ProsperFormValues>> = {
    name: T;
    label: string;
    parameter: Parameter;
    defaultValue?: any;
    errors: boolean;
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
    formFieldType: FormFieldType;
    valueOverride?: string;
    onChangeOverride?: (e: string, field: any) => void;
    options?: (string | number)[];
    optionValues?: string[];
    helperText?: string;
    required?: boolean;
    placeholder?: string;
    renderCustom?: (
        field: ControllerRenderProps<ProsperFormValues, T>,
        props: FormFieldProps<T>,
    ) => React.ReactElement | null;
    precise?: boolean;
    disabled?: boolean;
    optionDisplayFormatter?: (option: string | number) => string | number;
    isOptionDisabledCb?: (option: any) => boolean;
};
const requiredCustom = (value?: string) => (
    <span>
        {value && value} <span style={{color: 'red'}}>*</span>
    </span>
);

const RenderInput = <T extends FieldPath<ProsperFormValues>>({
    field,
    props,
}: {
    field: any;
    props: FormFieldProps<T>;
}) => {
    const {
        valueChanged,
        parameter,
        defaultValue,
        options,
        optionValues,
        label,
        name,
        errors,
        disabled,
        precise,
        helperText,
        placeholder,
        required,
        valueOverride,
        onChangeOverride,
        renderCustom,
        optionDisplayFormatter,
        isOptionDisabledCb,
    } = props;

    switch (props.formFieldType) {
        case FormFieldType.TEXT:
            return (
                <TextBox
                    {...field}
                    label={required ? requiredCustom(label) : label}
                    placeholder={placeholder}
                    onChange={(e) => {
                        let value: string | number = e;
                        if (value !== '') {
                            const numValue = Number(value);
                            if (!isNaN(numValue)) {
                                value = numValue;
                            }
                        }
                        field.onChange(value);
                        valueChanged(parameter, e);
                    }}
                    fullWidth
                    required={required ? requiredCustom('') : ''}
                    value={defaultValue ? defaultValue : parameter.value}
                    variant="outlined"
                    error={errors}
                    helperText={helperText}
                    disabled={disabled}
                />
            );
        case FormFieldType.NUMBER:
            return (
                <TextBox
                    {...field}
                    label={required ? requiredCustom(label) : label}
                    placeholder={placeholder}
                    onChange={
                        onChangeOverride
                            ? (e) => onChangeOverride(e, field)
                            : (e) => {
                                  let value: string | number = e;
                                  if (value !== '') {
                                      const numValue = Number(value);
                                      if (!isNaN(numValue)) {
                                          value = numValue;
                                      }
                                  }
                                  field.onChange(value);
                                  valueChanged(parameter, e);
                              }
                    }
                    fullWidth
                    value={defaultValue ? defaultValue : valueOverride ? valueOverride : parameter.value}
                    type={'number'}
                    variant="outlined"
                    error={errors}
                    helperText={helperText}
                    required={required ? requiredCustom('') : ''}
                    disabled={disabled}
                />
            );
        case FormFieldType.CURRENCY:
            return (
                <TextBox
                    {...field}
                    label={required ? requiredCustom(label) : label}
                    placeholder={placeholder}
                    onChange={
                        onChangeOverride
                            ? (e) => onChangeOverride(e, field)
                            : (e) => {
                                  let value: string | number = e;
                                  if (value !== '') {
                                      const numValue = Number(value);
                                      if (!isNaN(numValue)) {
                                          value = numValue;
                                      }
                                  }
                                  field.onChange(value);
                                  valueChanged(parameter, e);
                              }
                    }
                    InputProps={{inputComponent: CurrencyFormatCustom}}
                    fullWidth
                    value={defaultValue ? defaultValue : parameter.value}
                    variant="outlined"
                    error={errors}
                    helperText={helperText}
                    required={required ? requiredCustom('') : ''}
                    disabled={disabled}
                />
            );
        case FormFieldType.PERCENTAGE:
            return (
                <TextBox
                    {...field}
                    label={required ? requiredCustom(label) : label}
                    placeholder={placeholder}
                    onChange={(e) => {
                        let value: string | number = e;
                        if (value !== '') {
                            const numValue = Number(value);
                            if (!isNaN(numValue)) {
                                value = numValue;
                            }
                        }
                        field.onChange(value);
                        valueChanged(parameter, e);
                    }}
                    InputProps={{inputComponent: precise ? PercentageFormatPreciseCustom : PercentageFormatCustom}}
                    fullWidth
                    value={defaultValue ? defaultValue : parameter.value}
                    variant="outlined"
                    error={errors}
                    helperText={helperText}
                    required={required ? requiredCustom('') : ''}
                    disabled={disabled}
                />
            );
        case FormFieldType.SELECT:
            return (
                <SelectInputElement
                    param={{
                        label: label,
                        name: name,
                        options: options!,
                        optionValues: optionValues,
                        ...parameter,
                    }}
                    formType="single-quote"
                    required={required ? requiredCustom('') : ''}
                    defaultValue={defaultValue}
                    valueChanged={valueChanged}
                    form={true}
                    valueChangedForm={field.onChange}
                    disabled={disabled}
                    error={errors}
                    helperText={helperText}
                    optionDisplayFormatter={optionDisplayFormatter}
                    isOptionDisabledCb={isOptionDisabledCb}
                />
            );
        case FormFieldType.TEXTAREA:
        case FormFieldType.SWITCH:
            return (
                <>
                    <SwitchCustom
                        param={{
                            label: label,
                            name: name,
                            ...parameter,
                        }}
                        form={true}
                        valueChangedForm={field.onChange}
                        required={required ? requiredCustom('') : ''}
                        valueChanged={valueChanged}
                    />
                    {!!errors && <Typography color="error">{helperText}</Typography>}
                </>
            );

        case FormFieldType.CHECKBOX:
        case FormFieldType.DATE_PICKER:

        case FormFieldType.CUSTOM:
            if (renderCustom) {
                return renderCustom(field, props);
            } else return <></>;
        default:
            return null;
    }
};

const FormField = <T extends FieldPath<ProsperFormValues>>(props: FormFieldProps<T>) => {
    const {name, parameter, defaultValue} = props;
    const {control} = useFormContext<ProsperFormValues>();

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue ? defaultValue : parameter.value}
            render={({field}) => {
                const renderedInput = RenderInput({field, props});
                return renderedInput !== null ? renderedInput : <></>;
            }}
        />
    );
};

export default FormField;
